import React, { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FiCheck, FiChevronLeft, FiFileText, FiPaperclip, FiTrash2 } from 'react-icons/fi';
import { ROUTES } from '../../../app/routes';
import { useSubmissionCenterStats } from '../../../context/SubmissionCenterStatsContext';
import AqSelect from './AqSelect';
import { todayISO, type DocType } from './QuoteFieldsPanel';
import { parseAmount, toDateInput } from '../../acordsmartscan/acordSmartScanShared';
import { createQuote, fileToBase64, BUSINESS_TYPE_VALUES, oneYearLater, type CreateQuotePayload } from './submissionApi';
import '../../../styles/SubmissionCenter.css';
import '../../../styles/AddQuotePage.css';
import '../../../styles/AddOpportunityPage.css';

const MYAMP_BASE = process.env.REACT_APP_MyAMP_Url;

interface QuoteDoc { name: string; kind: string; ok: boolean; note: string; when: string; file?: File; }

interface LocationState {
  insuredDatabaseId?: string;
  accountName?: string;
}

/**
 * Add Quote — a standalone page for logging a new quote against an opportunity
 * that already exists, reached by expanding an opportunity in Submission
 * Center and clicking "Add Quote". Same fields/handlers as Step 3 of the "Add
 * an opportunity" wizard (AddOpportunityPage.tsx) — duplicated rather than
 * shared, same convention already used between those two, so neither page's
 * changes ripple into the other. No stepper here: the opportunity this quote
 * attaches to is already created, there's nothing to step back through.
 */
const AddQuotePage: React.FC = () => {
  const { oppId } = useParams<{ oppId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { notifyQuoteAdded } = useSubmissionCenterStats();
  const state = (location.state || {}) as LocationState;
  const insuredDatabaseId = state.insuredDatabaseId || '';
  const accountName = state.accountName || '';

  const [quoteNumber, setQuoteNumber] = useState('');
  const [quoteEffectiveDate, setQuoteEffectiveDate] = useState('');
  const [quoteExpirationDate, setQuoteExpirationDate] = useState('');
  const [quoteCarrierName, setQuoteCarrierName] = useState('');
  const [quoteBusinessType, setQuoteBusinessType] = useState('New Business');
  const [quotePremium, setQuotePremium] = useState('');
  const [quoteNote, setQuoteNote] = useState('');
  const [quoteDocs, setQuoteDocs] = useState<QuoteDoc[]>([]);
  const [trackingTransactionId, setTrackingTransactionId] = useState('');
  const quoteValid = !!(quoteNumber.trim() && quoteEffectiveDate && quoteCarrierName.trim() && quotePremium.trim());
  // Picking an effective date defaults expiration to one year later — the user can
  // still edit expiration afterward, this only fires on the effective date's own change.
  const handleQuoteEffectiveDateChange = (value: string) => {
    setQuoteEffectiveDate(value);
    if (value) setQuoteExpirationDate(oneYearLater(value));
  };

  const [documentType, setDocumentType] = useState<DocType>('DecPage');
  const [scanFile, setScanFile] = useState<File | null>(null);
  const [scanDragging, setScanDragging] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState('');
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [attachDragging, setAttachDragging] = useState(false);
  const attachInputRef = useRef<HTMLInputElement>(null);

  const onScanDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setScanDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setScanFile(f);
  };
  const onScanFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setScanFile(f);
    e.target.value = '';
  };

  const onScanAndPrefill = useCallback(async () => {
    if (!scanFile) return;
    setScanLoading(true);
    setScanError('');

    // opportunity-tracking-submission scans the dropped dec page/quote letter and
    // returns { transactionId, extracted: {...} } — extracted fields prefill the
    // Quote Details fields below.
    try {
      const trackingData = new FormData();
      trackingData.append('file', scanFile);
      trackingData.append('insuredId', insuredDatabaseId);
      trackingData.append('opportunityId', oppId || '');
      const trackingRes = await axios.post(
        `${MYAMP_BASE}/api/ams/opportunity-tracking-submission`,
        trackingData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } },
      );
      setTrackingTransactionId(trackingRes.data?.transactionId || '');
      const extracted = trackingRes.data?.extracted;
      if (extracted) {
        if (extracted.carrierName) setQuoteCarrierName(extracted.carrierName);
        if (extracted.premium) setQuotePremium(String(parseAmount(extracted.premium)));
        if (extracted.effectiveDate) setQuoteEffectiveDate(toDateInput(extracted.effectiveDate));
        if (extracted.expirationDate) setQuoteExpirationDate(toDateInput(extracted.expirationDate));
        if (extracted.quoteNumber) setQuoteNumber(extracted.quoteNumber);
        if (extracted.businessType && extracted.businessType in BUSINESS_TYPE_VALUES) {
          setQuoteBusinessType(extracted.businessType);
        }
        setScanFile(null);
      }
    }
    catch (trackErr) {
      console.log('opportunity-tracking-submission error:', trackErr);
      setScanError('Could not extract details from this document.');
    }
    finally {
      setScanLoading(false);
    }
  }, [scanFile, insuredDatabaseId, oppId]);

  const addDocsFromFiles = (files: File[]) => {
    if (!files.length) return;
    setQuoteDocs(prev => [...prev, ...files.map(f => ({
      name: f.name, kind: 'Attachment', ok: true, note: 'Attached manually', when: todayISO(), file: f,
    }))]);
  };
  const onAttachDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setAttachDragging(false);
    addDocsFromFiles(Array.from(e.dataTransfer.files || []));
  };
  const onAttachFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addDocsFromFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };
  const removeQuoteDoc = (idx: number) => setQuoteDocs(prev => prev.filter((_, i) => i !== idx));

  const [creatingQuote, setCreatingQuote] = useState(false);
  const [createQuoteError, setCreateQuoteError] = useState('');

  const finish = async () => {
    if (!quoteValid) return;
    setCreateQuoteError('');
    setCreatingQuote(true);
    try {
      // Only the first attachment travels with the quote — NowCerts'
      // UploadPolicyFile takes one file per call, and this is chained
      // as a single best-effort call the same way Note is.
      const firstDoc = quoteDocs.find(d => d.file);
      const fileContentBase64 = firstDoc?.file ? await fileToBase64(firstDoc.file) : undefined;
      const payload: CreateQuotePayload = {
        number: quoteNumber.trim(),
        effectiveDate: quoteEffectiveDate || undefined,
        expirationDate: quoteExpirationDate || undefined,
        carrierName: quoteCarrierName.trim() || undefined,
        businessType: BUSINESS_TYPE_VALUES[quoteBusinessType] ?? 0,
        premium: quotePremium ? Number(quotePremium) : undefined,
        insuredDatabaseId: insuredDatabaseId || undefined,
        opportunityIds: oppId ? [oppId] : undefined,
        note: quoteNote.trim() || undefined,
        fileName: firstDoc?.file?.name,
        fileContentBase64,
      };
      await createQuote(payload);
      if (oppId) notifyQuoteAdded({ opportunityId: oppId, quoteNumber: quoteNumber.trim() });
      navigate(ROUTES.CENTER_SUBMISSION_TRACKER);
    }
    catch {
      setCreateQuoteError('Failed to create the quote. Please try again.');
      setCreatingQuote(false);
    }
  };

  return (<div className="aq-page">
    <div className="aq-page__inner">
      <button type="button" className="cs-back-link" onClick={() => navigate(ROUTES.CENTER_SUBMISSION_TRACKER)}>
        <FiChevronLeft size={13}/> Back to Submission Center
      </button>

      <header className="aq-head">
        {accountName && <div className="aq-head__eyebrow">{accountName}</div>}
        <h1 className="aq-head__title">Add Quote</h1>
        <p className="aq-head__blurb">
          Log a new carrier quote for this opportunity, or scan a dec page or quote letter to prefill it.
        </p>
      </header>

      <section className="sc-panel__section aq-section">
        <h3 className="sc-panel__section-title">Scan a dec page or quote letter to prefill</h3>
        <div className="cs-tabs">
          <button
            type="button"
            className={`cs-tab${documentType === 'DecPage' ? ' cs-tab--active' : ''}`}
            onClick={() => setDocumentType('DecPage')}
          >
            Dec Page
          </button>
          <button
            type="button"
            className={`cs-tab${documentType === 'QuoteLetter' ? ' cs-tab--active' : ''}`}
            onClick={() => setDocumentType('QuoteLetter')}
          >
            Quote Letter
          </button>
        </div>
        <div
          className={`cs-dropzone aq-dropzone${scanDragging ? ' cs-dropzone--dragging' : ''}`}
          onDrop={onScanDrop}
          onDragOver={(e) => { e.preventDefault(); setScanDragging(true); }}
          onDragLeave={() => setScanDragging(false)}
        >
          {!scanFile ? (
            <>
              <div className="cs-dropzone__icon"><FiFileText size={22} /></div>
              <p className="cs-dropzone__title">
                Drag &amp; drop {documentType === 'DecPage' ? 'a dec page' : 'a quote letter'} PDF here
              </p>
              <p className="cs-dropzone__or">or</p>
              <button type="button" className="cs-browse-btn cs-browse-btn--sm" onClick={() => scanInputRef.current?.click()}>
                Browse Files
              </button>
              <input ref={scanInputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={onScanFileInput} />
            </>
          ) : (
            <div className="cs-file-card">
              <div className="cs-file-card__icon"><FiFileText size={18} /></div>
              <div className="cs-file-card__info">
                <span className="cs-file-card__name">{scanFile.name}</span>
                <span className="cs-file-card__size">{(scanFile.size / 1024).toFixed(0)} KB</span>
              </div>
              <button type="button" className="cs-file-card__delete" onClick={() => setScanFile(null)} title="Remove">
                <FiTrash2 size={14} />
              </button>
            </div>
          )}
        </div>
        {scanError && <p className="aq-error">{scanError}</p>}
        <button
          type="button"
          className="cs-browse-btn cs-browse-btn--sm aq-scan-btn"
          disabled={!scanFile || scanLoading}
          onClick={onScanAndPrefill}
        >
          {scanLoading ? 'Scanning…' : 'Scan & prefill'}
        </button>
      </section>

      <section className="sc-panel__section aq-section">
        <h3 className="sc-panel__section-title">Quote details</h3>
        <div className="aq-grid">
          <div className="aq-field">
            <label className="aq-label">Quote number *</label>
            <input className="glass-input" value={quoteNumber} onChange={(e) => setQuoteNumber(e.target.value)} placeholder="e.g. SUB-882301"/>
          </div>
          <div className="aq-field">
            <label className="aq-label">Effective date *</label>
            <input type="date" className="glass-input" value={quoteEffectiveDate} onChange={(e) => handleQuoteEffectiveDateChange(e.target.value)}/>
          </div>
          <div className="aq-field">
            <label className="aq-label">Expiration date</label>
            <input type="date" className="glass-input" value={quoteExpirationDate} onChange={(e) => setQuoteExpirationDate(e.target.value)}/>
          </div>
          <div className="aq-field">
            <label className="aq-label">Carrier name *</label>
            <input className="glass-input" value={quoteCarrierName} onChange={(e) => setQuoteCarrierName(e.target.value)} placeholder="e.g. Travelers"/>
          </div>
          <div className="aq-field">
            <label className="aq-label">Business type</label>
            <AqSelect value={quoteBusinessType} onChange={setQuoteBusinessType} options={Object.keys(BUSINESS_TYPE_VALUES)} />
          </div>
          <div className="aq-field">
            <label className="aq-label">Premium *</label>
            <input type="number" min="0" className="glass-input" value={quotePremium} onChange={(e) => setQuotePremium(e.target.value)} placeholder="e.g. 171450"/>
          </div>
        </div>
      </section>

      <section className="sc-panel__section aq-section">
        <h3 className="sc-panel__section-title">Note</h3>
        <textarea
          className="glass-input aq-note"
          rows={3}
          value={quoteNote}
          onChange={(e) => setQuoteNote(e.target.value)}
          placeholder="Add a note about this quote…"
        />
      </section>

      <section className="sc-panel__section aq-section">
        <h3 className="sc-panel__section-title">Documents</h3>
        {quoteDocs.length === 0 ? (
          <p className="sc-panel__note">No documents attached yet.</p>
        ) : (
          <ul className="sc-panel__list">
            {quoteDocs.map((d, i) => (
              <li key={`${d.name}-${i}`}>
                <span className="sc-panel__list-icon"><FiCheck size={12} /></span>
                <span className="sc-panel__list-main">
                  {d.name}
                  <span className="sc-panel__list-meta">{d.kind} · {d.note}</span>
                </span>
                <button type="button" className="aq-doc-remove" onClick={() => removeQuoteDoc(i)} aria-label={`Remove ${d.name}`}>
                  <FiTrash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div
          className={`aq-attach${attachDragging ? ' aq-attach--dragging' : ''}`}
          onDrop={onAttachDrop}
          onDragOver={(e) => { e.preventDefault(); setAttachDragging(true); }}
          onDragLeave={() => setAttachDragging(false)}
        >
          <button type="button" className="cs-browse-btn cs-browse-btn--sm" onClick={() => attachInputRef.current?.click()}>
            <FiPaperclip size={13} /> Attach documents
          </button>
          <span className="aq-attach__hint">or drag files here — attaches without scanning. You can attach as many as you need.</span>
          <input ref={attachInputRef} type="file" multiple style={{ display: 'none' }} onChange={onAttachFileInput} />
        </div>
      </section>

      {createQuoteError && <p className="aq-note" style={{ color: '#f87171' }}>{createQuoteError}</p>}

      <div className="aq-submitbar">
        <button type="button" className="glass-btn aq-cancel" disabled={creatingQuote} onClick={() => navigate(ROUTES.CENTER_SUBMISSION_TRACKER)}>
          Cancel
        </button>
        <button type="button" className="cs-browse-btn" disabled={!quoteValid || creatingQuote} onClick={finish}>
          {creatingQuote ? 'Creating…' : 'Add Quote'}
        </button>
      </div>
    </div>
  </div>);
};

export default AddQuotePage;
